(function () {
  'use strict';

  var STORAGE_KEY = 'myd_wishlist_v1';
  var DEFAULT_BOARD_ID = 'default';
  var DEFAULT_BOARD_NAME = 'My Wishlist';
  var SYNC_URL = '/apps/wishlist-sync';
  var SYNC_DEBOUNCE_MS = 800;
  var MAX_BOARDS = 5;
  var MAX_ITEMS_PER_BOARD = 25;

  function defaultState() {
    return { boards: [{ id: DEFAULT_BOARD_ID, name: DEFAULT_BOARD_NAME, items: [] }], updatedAt: 0 };
  }

  function loadState() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.boards) || !parsed.boards.length) return null;
      if (typeof parsed.updatedAt !== 'number') parsed.updatedAt = 0;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function saveState(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {}
  }

  var state = loadState() || defaultState();

  function emit() {
    document.dispatchEvent(new CustomEvent('wishlist:updated'));
  }

  /**
   * Union by board id, union items within each board. Only used once, as a
   * safety net when the server's stored state predates updatedAt tracking
   * (nothing to compare timestamps against yet) — so a first sync after this
   * update can't silently drop data either side already had.
   */
  function unionBoards(a, b) {
    var byId = {};
    var order = [];
    function ingest(boards) {
      (boards || []).forEach(function (board) {
        if (!byId[board.id]) {
          byId[board.id] = { id: board.id, name: board.name, items: board.items.slice() };
          order.push(board.id);
        } else {
          var existing = byId[board.id];
          existing.name = board.name || existing.name;
          var seen = {};
          existing.items.forEach(function (h) {
            seen[h] = true;
          });
          board.items.forEach(function (h) {
            if (!seen[h]) {
              seen[h] = true;
              existing.items.push(h);
            }
          });
        }
      });
    }
    ingest(a && a.boards);
    ingest(b && b.boards);
    return { boards: order.map(function (id) { return byId[id]; }) };
  }

  /**
   * A pure union can only ever add items, never remove them — so a deletion
   * synced from one device would get silently re-added by the next merge
   * against another device's older copy. Last-write-wins by updatedAt fixes
   * that: whichever side changed more recently is adopted wholesale (removals
   * included), instead of unioning both sides' item lists together.
   * Returns { result, migrated } — migrated is true exactly once, the first
   * time either side is seen without a real updatedAt (legacy data predating
   * this change), so the caller can push the newly-stamped result straight
   * back to the server instead of waiting for the next organic edit.
   */
  function reconcile(local, remote) {
    if (!remote || !Array.isArray(remote.boards)) return { result: local, migrated: false };
    var remoteAt = typeof remote.updatedAt === 'number' ? remote.updatedAt : 0;
    var localAt = typeof local.updatedAt === 'number' ? local.updatedAt : 0;
    if (!remoteAt && !localAt) {
      var merged = unionBoards(local, remote);
      merged.updatedAt = Date.now();
      return { result: merged, migrated: true };
    }
    return {
      result: remoteAt >= localAt ? { boards: remote.boards, updatedAt: remoteAt } : local,
      migrated: false
    };
  }

  function itemSignature(s) {
    return JSON.stringify(
      ((s && s.boards) || []).map(function (b) {
        return [b.id, b.name, b.items];
      })
    );
  }

  function fetchWithTimeout(url, options, ms) {
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = window.setTimeout(function () {
      if (ctrl) ctrl.abort();
    }, ms || 8000);
    var opts = Object.assign({}, options || {});
    if (ctrl) opts.signal = ctrl.signal;
    return fetch(url, opts).finally(function () {
      window.clearTimeout(timer);
    });
  }

  function createPool(limit) {
    var active = 0;
    var queue = [];
    function kick() {
      if (active >= limit || !queue.length) return;
      active += 1;
      var job = queue.shift();
      Promise.resolve()
        .then(job.fn)
        .then(job.resolve, job.reject)
        .then(function () {
          active -= 1;
          kick();
        });
    }
    return function enqueue(fn) {
      return new Promise(function (resolve, reject) {
        queue.push({ fn: fn, resolve: resolve, reject: reject });
        kick();
      });
    };
  }

  var enqueueProduct = createPool(3);
  var enqueueRating = createPool(2);

  var syncTimer = null;
  function queueSync() {
    if (!window.MYD_CUSTOMER_LOGGED_IN) return;
    if (syncTimer) window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(function () {
      syncTimer = null;
      var before = itemSignature(state);
      fetchWithTimeout(
        SYNC_URL,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ boards: state.boards, updatedAt: state.updatedAt }),
          credentials: 'same-origin'
        },
        10000
      )
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .then(function (serverState) {
          if (!serverState || !Array.isArray(serverState.boards)) return;
          // The Lambda already resolved last-write-wins server-side and wrote
          // the outcome — adopt its response as-is rather than merging again.
          state = { boards: serverState.boards, updatedAt: serverState.updatedAt || state.updatedAt };
          saveState(state);
          if (itemSignature(state) !== before) emit();
        })
        .catch(function () {});
    }, SYNC_DEBOUNCE_MS);
  }

  function pullFromServer() {
    if (!window.MYD_CUSTOMER_LOGGED_IN) return;
    fetchWithTimeout(SYNC_URL, { method: 'GET', credentials: 'same-origin' }, 10000)
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (serverState) {
        if (!serverState || !Array.isArray(serverState.boards) || !serverState.boards.length) return;
        var before = itemSignature(state);
        var outcome = reconcile(state, serverState);
        state = outcome.result;
        saveState(state);
        if (itemSignature(state) !== before) emit();
        else updateNavCount();
        // First time either side lacked a real updatedAt, reconcile() just
        // unioned once and stamped it locally — push that straight back so
        // the server migrates immediately instead of on the next edit.
        if (outcome.migrated) queueSync();
      })
      .catch(function () {});
  }

  function persist() {
    state.updatedAt = Date.now();
    saveState(state);
    emit();
    queueSync();
  }

  var Wishlist = {
    getBoards: function () {
      return state.boards;
    },
    getBoard: function (id) {
      for (var i = 0; i < state.boards.length; i++) {
        if (state.boards[i].id === id) return state.boards[i];
      }
      return null;
    },
    /** Unique product handles across all boards (most recent boards first). */
    allHandles: function () {
      var seen = {};
      var out = [];
      state.boards.forEach(function (b) {
        (b.items || []).forEach(function (h) {
          if (!h || seen[h]) return;
          seen[h] = true;
          out.push(h);
        });
      });
      return out;
    },
    totalCount: function () {
      return Wishlist.allHandles().length;
    },
    hasItem: function (handle) {
      return state.boards.some(function (b) {
        return b.items.indexOf(handle) !== -1;
      });
    },
    boardsContaining: function (handle) {
      return state.boards.filter(function (b) {
        return b.items.indexOf(handle) !== -1;
      }).map(function (b) {
        return b.id;
      });
    },
    createBoard: function (name) {
      name = (name || '').trim();
      if (!name) return null;
      if (state.boards.length >= MAX_BOARDS) {
        window.alert('You can have up to ' + MAX_BOARDS + ' wishlist boards.');
        return null;
      }
      var id = 'b_' + Math.random().toString(36).slice(2, 9);
      state.boards.push({ id: id, name: name, items: [] });
      persist();
      return id;
    },
    renameBoard: function (id, name) {
      name = (name || '').trim();
      var board = this.getBoard(id);
      if (!board || !name) return;
      board.name = name;
      persist();
    },
    deleteBoard: function (id) {
      if (id === DEFAULT_BOARD_ID) return;
      var board = this.getBoard(id);
      if (!board) return;
      var fallback = this.getBoard(DEFAULT_BOARD_ID);
      board.items.forEach(function (h) {
        if (fallback.items.indexOf(h) === -1) fallback.items.push(h);
      });
      state.boards = state.boards.filter(function (b) {
        return b.id !== id;
      });
      persist();
    },
    addTo: function (boardId, handle) {
      var board = this.getBoard(boardId) || this.getBoard(DEFAULT_BOARD_ID);
      if (!board || !handle) return;
      if (board.items.indexOf(handle) === -1) {
        if (board.items.length >= MAX_ITEMS_PER_BOARD) {
          window.alert('"' + board.name + '" is full (max ' + MAX_ITEMS_PER_BOARD + ' items).');
          return;
        }
        board.items.push(handle);
      }
      persist();
    },
    removeFrom: function (boardId, handle) {
      var board = this.getBoard(boardId);
      if (!board) return;
      board.items = board.items.filter(function (h) {
        return h !== handle;
      });
      persist();
    },
    removeEverywhere: function (handle) {
      state.boards.forEach(function (b) {
        b.items = b.items.filter(function (h) {
          return h !== handle;
        });
      });
      persist();
    },
    moveItem: function (handle, fromId, toId) {
      var from = this.getBoard(fromId);
      var to = this.getBoard(toId) || this.getBoard(DEFAULT_BOARD_ID);
      if (!from || !to) return;
      if (to.items.indexOf(handle) === -1 && to.items.length >= MAX_ITEMS_PER_BOARD) {
        window.alert('"' + to.name + '" is full (max ' + MAX_ITEMS_PER_BOARD + ' items).');
        return;
      }
      from.items = from.items.filter(function (h) {
        return h !== handle;
      });
      if (to.items.indexOf(handle) === -1) to.items.push(handle);
      persist();
    },
    toggle: function (boardId, handle) {
      var board = this.getBoard(boardId) || this.getBoard(DEFAULT_BOARD_ID);
      if (!board || !handle) return;
      var idx = board.items.indexOf(handle);
      if (idx === -1) {
        if (board.items.length >= MAX_ITEMS_PER_BOARD) {
          window.alert('"' + board.name + '" is full (max ' + MAX_ITEMS_PER_BOARD + ' items).');
          return;
        }
        board.items.push(handle);
      } else {
        board.items.splice(idx, 1);
      }
      persist();
    }
  };

  window.MydWishlist = Wishlist;

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      return theme.Currency.formatMoney(cents, (window.shopSettings && window.shopSettings.moneyFormat) || '${{amount}}');
    }
    return '₹' + Math.round(cents / 100).toLocaleString('en-IN');
  }

  function productImageUrl(product) {
    var src = product && (product.featured_image || (product.images && product.images[0]));
    if (!src) return '';
    if (typeof src === 'object') src = src.src || '';
    if (!src) return '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    return src;
  }

  function fitFromTags(product) {
    var tags = (product && product.tags) || [];
    for (var i = 0; i < tags.length; i++) {
      if (/\bfit\b/i.test(tags[i])) return tags[i];
    }
    return '';
  }

  function firstAvailableVariant(product) {
    var variants = (product && product.variants) || [];
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) return variants[i];
    }
    return variants[0] || null;
  }

  var ratingCache = {};
  function fetchRating(handle) {
    if (ratingCache[handle]) return ratingCache[handle];
    ratingCache[handle] = enqueueRating(function () {
      return fetchWithTimeout(
        '/products/' + handle + '?section_id=myd-wishlist-rating',
        { credentials: 'same-origin' },
        8000
      )
      .then(function (r) {
        return r.ok ? r.text() : '';
      })
      .then(function (html) {
        if (!html) return null;
        var box = document.createElement('div');
        box.innerHTML = html;
        var root =
          box.querySelector('[data-myd-wishlist-rating]') ||
          box.querySelector('[data-average-rating]');
        if (!root) return null;
        var avg = Number.parseFloat(root.getAttribute('data-average-rating') || '0');
        var count = Number.parseInt(root.getAttribute('data-number-of-reviews') || '0', 10);
        if (!(avg > 0)) {
          var prev =
            box.querySelector('.jdgm-prev-badge[data-average-rating]') ||
            box.querySelector('[data-average-rating]');
          if (prev) {
            avg = Number.parseFloat(prev.getAttribute('data-average-rating') || '0');
            count = Number.parseInt(prev.getAttribute('data-number-of-reviews') || '0', 10) || count;
          }
        }
        if (!(avg > 0)) return null;
        return { avg: avg, count: count || 0 };
      })
      .catch(function () {
        return null;
      });
    });
    return ratingCache[handle];
  }

  function applyWishlistRating(card, rating) {
    if (!card || !rating || !(rating.avg > 0)) return;
    var el = card.querySelector('[data-myd-card-rating]');
    var scoreEl = card.querySelector('[data-myd-card-rating-score]');
    if (!el || !scoreEl) return;
    var rounded = Math.round(rating.avg * 10) / 10;
    var score = Number.isInteger(rounded) ? rounded.toFixed(1) : String(rounded);
    scoreEl.textContent = score;
    el.classList.remove('is-empty');
    el.removeAttribute('aria-hidden');
    el.removeAttribute('tabindex');
    el.dataset.mydRatingReady = '1';
    el.setAttribute(
      'aria-label',
      rating.count > 0
        ? 'Rated ' + score + ' from ' + rating.count + (rating.count === 1 ? ' review' : ' reviews')
        : 'Rated ' + score
    );
    var atc = card.querySelector('[data-myd-quick-add]');
    if (atc) {
      atc.setAttribute('data-rating', String(rating.avg));
      if (rating.count > 0) atc.setAttribute('data-rating-count', String(rating.count));
    }
  }

  function kickJudgeMeRatings(root) {
    try {
      window.MYDJudgeMe?.scheduleEnhance?.();
    } catch (e) {}
    try {
      document.dispatchEvent(new CustomEvent('myd:card-ratings-refresh'));
    } catch (e) {}
    try {
      var jdgm = window.jdgm;
      if (!jdgm || !jdgm.$ || typeof jdgm._setupLoadReviewsEventsFor !== 'function') return;
      var scope = root && root.querySelectorAll ? root : document;
      scope.querySelectorAll('.jdgm-preview-badge').forEach(function (badge) {
        badge.classList.remove('jdgm--done-setup-widget', 'jdgm--done-setup');
        jdgm._setupLoadReviewsEventsFor(jdgm.$(badge));
      });
    } catch (e) {}
  }

  function updateNavCount() {
    var count = Wishlist.totalCount();
    document.querySelectorAll('[data-myd-wishlist-count]').forEach(function (el) {
      if (count > 0) {
        el.textContent = String(count);
        el.removeAttribute('hidden');
        el.hidden = false;
      } else {
        el.textContent = '';
        el.setAttribute('hidden', '');
        el.hidden = true;
      }
    });
  }

  var productCache = {};
  function getProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = enqueueProduct(function () {
        return fetchWithTimeout('/products/' + handle + '.js', { credentials: 'same-origin' }, 8000).then(
          function (r) {
            return r.ok ? r.json() : null;
          }
        );
      }).catch(function () {
        return null;
      });
    }
    return productCache[handle];
  }

  function boardModal() {
    var modal = document.getElementById('myd-wishlist-board-modal');
    if (!modal) return null;
    if (modal.parentNode !== document.body) document.body.appendChild(modal);
    if (modal.dataset.bound) return modal;
    modal.dataset.bound = '1';

    var form = modal.querySelector('[data-myd-wish-modal-form]');
    var titleEl = modal.querySelector('#myd-wish-modal-title');
    var textEl = modal.querySelector('[data-myd-wish-modal-text]');
    var fieldEl = modal.querySelector('[data-myd-wish-modal-field]');
    var inputEl = modal.querySelector('[data-myd-wish-modal-input]');
    var confirmEl = modal.querySelector('[data-myd-wish-modal-confirm]');
    var cancelEl = modal.querySelector('[data-myd-wish-modal-cancel]');
    var pending = null;

    function close() {
      pending = null;
      modal.hidden = true;
      modal.classList.remove('is-danger');
      document.body.classList.remove('myd-wishlist-modal-open');
    }

    function open(opts) {
      pending = opts || {};
      titleEl.textContent = pending.title || 'Name your new board';
      if (pending.message) {
        textEl.hidden = false;
        textEl.textContent = pending.message;
      } else {
        textEl.hidden = true;
        textEl.textContent = '';
      }
      if (pending.hideInput) {
        fieldEl.hidden = true;
        inputEl.removeAttribute('required');
        inputEl.value = '';
      } else {
        fieldEl.hidden = false;
        inputEl.setAttribute('required', '');
        inputEl.value = pending.value || '';
        inputEl.placeholder = pending.placeholder || 'e.g. Onam looks';
      }
      confirmEl.textContent = pending.confirmLabel || 'Create';
      confirmEl.hidden = Boolean(pending.hideConfirm);
      if (cancelEl) cancelEl.textContent = pending.hideConfirm ? 'OK' : 'Cancel';
      modal.classList.toggle('is-danger', Boolean(pending.danger));
      confirmEl.classList.toggle('myd-wishlist-modal__btn--danger', Boolean(pending.danger));
      confirmEl.classList.toggle('myd-wishlist-modal__btn--solid', !pending.danger);
      modal.hidden = false;
      document.body.classList.add('myd-wishlist-modal-open');
      window.setTimeout(function () {
        if (!pending.hideInput && inputEl) {
          inputEl.focus();
          inputEl.select();
        } else if (confirmEl && !confirmEl.hidden) {
          confirmEl.focus();
        }
      }, 30);
    }

    modal.openBoard = open;
    modal.closeBoard = close;

    modal.querySelectorAll('[data-myd-wish-modal-dismiss]').forEach(function (el) {
      el.addEventListener('click', function (event) {
        event.preventDefault();
        close();
      });
    });

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (!pending) return;
      var name = inputEl ? String(inputEl.value || '').trim() : '';
      if (!pending.hideInput && !name) {
        inputEl.focus();
        return;
      }
      var done = pending.onConfirm;
      close();
      if (typeof done === 'function') done(name);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !modal.hidden) {
        event.preventDefault();
        close();
      }
    });

    return modal;
  }

  function initPanel(root) {
    var boardsEl = root.querySelector('[data-myd-wishlist-boards]');
    var gridEl = root.querySelector('[data-myd-wishlist-grid]');
    var emptyEl = root.querySelector('[data-myd-wishlist-empty]');
    var activeBoardId = DEFAULT_BOARD_ID;
    var modal = boardModal();

    function closeMoveMenus(except) {
      root.querySelectorAll('.myd-wishlist__move.is-open').forEach(function (wrap) {
        if (except && wrap === except) return;
        wrap.classList.remove('is-open');
        var btn = wrap.querySelector('.myd-wishlist__card-move');
        var menu = wrap.querySelector('.myd-wishlist__move-menu');
        if (btn) btn.setAttribute('aria-expanded', 'false');
        if (menu) menu.hidden = true;
      });
    }

    function buildMoveMenu(wrap, handle, boardId) {
      var menu = wrap.querySelector('.myd-wishlist__move-menu');
      if (!menu) return;
      menu.innerHTML = '';

      var heading = document.createElement('p');
      heading.className = 'myd-wishlist__move-heading';
      heading.textContent = 'Move to';
      menu.appendChild(heading);

      var targets = Wishlist.getBoards().filter(function (b) {
        return b.id !== boardId;
      });

      if (!targets.length) {
        var empty = document.createElement('p');
        empty.className = 'myd-wishlist__move-empty';
        empty.textContent = 'Create another board first';
        menu.appendChild(empty);
      } else {
        targets.forEach(function (board) {
          var option = document.createElement('button');
          option.type = 'button';
          option.className = 'myd-wishlist__move-option';
          option.textContent = board.name;
          option.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            if (event.stopImmediatePropagation) event.stopImmediatePropagation();
            Wishlist.moveItem(handle, boardId, board.id);
            closeMoveMenus();
          });
          menu.appendChild(option);
        });
      }
    }

    function render() {
      closeMoveMenus();
      var boards = Wishlist.getBoards();
      if (!boards.some(function (b) { return b.id === activeBoardId; })) {
        activeBoardId = DEFAULT_BOARD_ID;
      }
      renderPills(boards);
      renderGrid(boards);
    }

    function renderPills(boards) {
      boardsEl.innerHTML = '';

      boards.forEach(function (board) {
        var pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'myd-wishlist__board-pill' + (board.id === activeBoardId ? ' is-active' : '');
        pill.setAttribute('role', 'tab');
        pill.setAttribute('aria-selected', board.id === activeBoardId ? 'true' : 'false');

        var label = document.createElement('span');
        label.textContent = board.name;
        var count = document.createElement('span');
        count.className = 'myd-wishlist__board-pill-count';
        count.textContent = String(board.items.length);
        pill.appendChild(label);
        pill.appendChild(count);

        if (board.id !== DEFAULT_BOARD_ID) {
          pill.title = 'Double-click to rename, shift-click to delete';
        }

        pill.addEventListener('click', function (e) {
          if (e.shiftKey && board.id !== DEFAULT_BOARD_ID) {
            if (!modal) return;
            modal.openBoard({
              title: 'Delete board?',
              message: '"' + board.name + '" will be removed. Items move to My Wishlist.',
              hideInput: true,
              danger: true,
              confirmLabel: 'Delete',
              onConfirm: function () {
                Wishlist.deleteBoard(board.id);
                if (activeBoardId === board.id) activeBoardId = DEFAULT_BOARD_ID;
                render();
              }
            });
            return;
          }
          activeBoardId = board.id;
          render();
        });

        if (board.id !== DEFAULT_BOARD_ID) {
          pill.addEventListener('dblclick', function () {
            if (!modal) return;
            modal.openBoard({
              title: 'Rename board',
              value: board.name,
              confirmLabel: 'Save',
              onConfirm: function (next) {
                Wishlist.renameBoard(board.id, next);
              }
            });
          });
        }

        boardsEl.appendChild(pill);
      });

      var addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'myd-wishlist__board-add';
      addBtn.textContent = '+ New board';
      addBtn.addEventListener('click', function () {
        if (!modal) return;
        if (Wishlist.getBoards().length >= MAX_BOARDS) {
          modal.openBoard({
            title: 'Board limit reached',
            message: 'You can have up to ' + MAX_BOARDS + ' wishlist boards.',
            hideInput: true,
            hideConfirm: true
          });
          return;
        }
        modal.openBoard({
          title: 'Name your new board',
          placeholder: 'e.g. Onam looks',
          confirmLabel: 'Create',
          onConfirm: function (name) {
            var id = Wishlist.createBoard(name);
            if (id) {
              activeBoardId = id;
              render();
            }
          }
        });
      });
      boardsEl.appendChild(addBtn);
    }

    function renderGrid(boards) {
      var board = boards.filter(function (b) { return b.id === activeBoardId; })[0] || boards[0];
      // items are appended (push) as they're wishlisted, so the newest is
      // last in the array — reverse for display so it shows first.
      var handles = board ? board.items.slice().reverse() : [];
      var signature = activeBoardId + ':' + handles.join('|');
      if (gridEl.dataset.mydWishSig === signature && gridEl.querySelector('.myd-wishlist__card')) {
        return;
      }
      gridEl.dataset.mydWishSig = signature;

      gridEl.innerHTML = '';

      if (!handles.length) {
        gridEl.hidden = true;
        emptyEl.hidden = false;
        return;
      }

      emptyEl.hidden = true;
      gridEl.hidden = false;

      var pending = handles.map(function (handle) {
        var card = document.createElement('article');
        card.className = 'myd-search-card myd-wishlist__card myd-wishlist__card--skel';
        card.setAttribute('data-product-handle', handle);
        card.setAttribute('aria-busy', 'true');
        card.innerHTML =
          '<div class="myd-wishlist__skel-media" aria-hidden="true"></div>' +
          '<div class="myd-wishlist__skel-body" aria-hidden="true">' +
          '<span class="myd-wishlist__skel-line myd-wishlist__skel-line--type"></span>' +
          '<span class="myd-wishlist__skel-line myd-wishlist__skel-line--title"></span>' +
          '<span class="myd-wishlist__skel-line myd-wishlist__skel-line--price"></span>' +
          '<span class="myd-wishlist__skel-line myd-wishlist__skel-line--cta"></span>' +
          '</div>';
        gridEl.appendChild(card);
        return hydrateCard(card, handle, board.id);
      });
      Promise.all(pending).then(function () {
        if (!gridEl.querySelector('.myd-wishlist__card')) {
          gridEl.hidden = true;
          emptyEl.hidden = false;
          return;
        }
        var later = window.requestIdleCallback || function (fn) { window.setTimeout(fn, 400); };
        later(function () {
          kickJudgeMeRatings(gridEl);
        });
      });
    }

    function hydrateCard(card, handle, boardId) {
      return getProduct(handle).then(function (product) {
        if (!product) {
          card.remove();
          return;
        }

        card.removeAttribute('aria-busy');

        var url = product.url || '/products/' + handle;
        var variant = firstAvailableVariant(product);
        var soldOut = product.available === false || !variant || variant.available === false;
        var imgUrl = productImageUrl(product);
        var fit = fitFromTags(product);
        var compare = product.compare_at_price || 0;
        var price = product.price || 0;
        var discountPct = 0;
        if (compare > price && compare > 0) {
          discountPct = Math.round(((compare - price) * 100) / compare);
        }

        card.className = 'myd-search-card myd-wishlist__card' + (soldOut ? ' is-sold-out' : '');
        card.setAttribute('data-product-handle', handle);
        card.setAttribute('data-product-id', String(product.id || ''));
        if (variant && variant.id) card.setAttribute('data-variant-id', String(variant.id));

        var media = document.createElement('div');
        media.className = 'myd-search-card__media';

        var mediaLink = document.createElement('a');
        mediaLink.className = 'myd-search-card__media-link';
        mediaLink.href = url;
        mediaLink.setAttribute('aria-label', product.title || handle);

        if (imgUrl) {
          var img = document.createElement('img');
          img.className = 'myd-search-card__image myd-search-card__image--primary';
          img.src = imgUrl;
          img.alt = product.title || '';
          img.loading = 'lazy';
          img.width = 360;
          img.height = 450;
          img.decoding = 'async';
          mediaLink.appendChild(img);
        }
        media.appendChild(mediaLink);

        if (!soldOut && discountPct >= 1) {
          var off = document.createElement('div');
          off.className = 'myd-search-card__discount';
          off.textContent = discountPct + '% OFF';
          media.appendChild(off);
        }

        if (fit && !soldOut) {
          var badge = document.createElement('span');
          badge.className = 'myd-search-card__badge';
          badge.textContent = fit;
          media.appendChild(badge);
        }

        var removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'myd-wishlist__card-remove';
        removeBtn.setAttribute('aria-label', 'Remove from wishlist');
        removeBtn.innerHTML =
          '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2 2l8 8M10 2L2 10" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';
        removeBtn.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          if (event.stopImmediatePropagation) event.stopImmediatePropagation();
          Wishlist.removeFrom(boardId, handle);
        });
        media.appendChild(removeBtn);

        var body = document.createElement('div');
        body.className = 'myd-search-card__body';

        var typeRow = document.createElement('div');
        typeRow.className = 'myd-search-card__type-row';
        var typeEl = document.createElement(product.type ? 'p' : 'span');
        typeEl.className = 'myd-search-card__type';
        if (product.type) typeEl.textContent = product.type;
        else typeEl.setAttribute('aria-hidden', 'true');
        typeRow.appendChild(typeEl);

        var rating = document.createElement('a');
        rating.href = url;
        rating.className = 'myd-search-card__rating is-empty';
        rating.setAttribute('data-myd-card-rating', '');
        rating.setAttribute('data-product-id', String(product.id || ''));
        rating.setAttribute('aria-label', 'Product rating');
        rating.setAttribute('aria-hidden', 'true');
        rating.tabIndex = -1;
        var star = document.createElement('span');
        star.className = 'myd-search-card__rating-star';
        star.setAttribute('aria-hidden', 'true');
        star.textContent = '\u2605';
        var score = document.createElement('span');
        score.className = 'myd-search-card__rating-score';
        score.setAttribute('data-myd-card-rating-score', '');
        var jdgmWrap = document.createElement('span');
        jdgmWrap.className = 'myd-search-card__jdgm';
        jdgmWrap.setAttribute('aria-hidden', 'true');
        var jdgmBadge = document.createElement('div');
        jdgmBadge.className = 'jdgm-widget jdgm-preview-badge';
        jdgmBadge.setAttribute('data-id', String(product.id || ''));
        jdgmBadge.setAttribute('data-product-id', String(product.id || ''));
        jdgmBadge.setAttribute('data-auto-install', 'false');
        jdgmWrap.appendChild(jdgmBadge);
        rating.appendChild(star);
        rating.appendChild(score);
        rating.appendChild(jdgmWrap);
        typeRow.appendChild(rating);
        body.appendChild(typeRow);

        var titleRow = document.createElement('div');
        titleRow.className = 'myd-search-card__title-row';
        var info = document.createElement('a');
        info.className = 'myd-search-card__info';
        info.href = url;
        var title = document.createElement('h3');
        title.className = 'myd-search-card__title';
        title.textContent = product.title || handle;
        info.appendChild(title);
        titleRow.appendChild(info);
        body.appendChild(titleRow);

        var priceRow = document.createElement('div');
        priceRow.className = 'myd-search-card__price myd-wishlist__price-row';
        var current = document.createElement('span');
        current.className = 'myd-search-card__price-current';
        current.textContent = formatMoney(price);
        priceRow.appendChild(current);
        if (compare > price) {
          var was = document.createElement('span');
          was.className = 'myd-search-card__price-compare';
          was.textContent = formatMoney(compare);
          priceRow.appendChild(was);
        }

        var boards = Wishlist.getBoards();
        if (boards.length > 1) {
          var moveWrap = document.createElement('div');
          moveWrap.className = 'myd-wishlist__move';

          var moveBtn = document.createElement('button');
          moveBtn.type = 'button';
          moveBtn.className = 'myd-wishlist__card-move';
          moveBtn.setAttribute('aria-label', 'Move to another wishlist');
          moveBtn.setAttribute('aria-expanded', 'false');
          moveBtn.setAttribute('aria-haspopup', 'menu');
          moveBtn.innerHTML =
            '<svg viewBox="0 0 14 14" aria-hidden="true"><path d="M2 7h8M7 3l4 4-4 4M2 2v10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

          var moveMenu = document.createElement('div');
          moveMenu.className = 'myd-wishlist__move-menu';
          moveMenu.setAttribute('role', 'menu');
          moveMenu.hidden = true;

          moveBtn.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            if (event.stopImmediatePropagation) event.stopImmediatePropagation();
            var open = moveWrap.classList.contains('is-open');
            closeMoveMenus();
            if (!open) {
              buildMoveMenu(moveWrap, handle, boardId);
              moveWrap.classList.add('is-open');
              moveBtn.setAttribute('aria-expanded', 'true');
              moveMenu.hidden = false;
            }
          });

          moveWrap.appendChild(moveBtn);
          moveWrap.appendChild(moveMenu);
          priceRow.appendChild(moveWrap);
        }

        body.appendChild(priceRow);

        var atc = document.createElement('button');
        atc.type = 'button';
        atc.className = 'myd-search-card__view-details';
        atc.setAttribute('data-myd-quick-add', '');
        atc.setAttribute('data-product-handle', handle);
        atc.textContent = soldOut ? 'Sold Out' : 'Add to cart';
        if (soldOut) {
          atc.disabled = true;
          atc.setAttribute('aria-disabled', 'true');
          atc.setAttribute('data-sold-out', 'true');
        } else {
          atc.setAttribute('aria-disabled', 'false');
          atc.setAttribute('data-sold-out', 'false');
        }

        card.innerHTML = '';
        card.appendChild(media);
        card.appendChild(body);
        card.appendChild(atc);

        return fetchRating(handle).then(function (rating) {
          applyWishlistRating(card, rating);
        });
      });
    }

    document.addEventListener('click', function (event) {
      if (event.target.closest && event.target.closest('.myd-wishlist__move')) return;
      closeMoveMenus();
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeMoveMenus();
    });

    document.addEventListener('wishlist:updated', render);
    render();
  }

  document.addEventListener('wishlist:updated', updateNavCount);
  updateNavCount();
  document.querySelectorAll('[data-myd-wishlist-root]').forEach(initPanel);
  pullFromServer();

  window.MYDWishlist = Wishlist;

  document.addEventListener(
    'click',
    function (event) {
      var btn = event.target.closest && event.target.closest('.myd-wishlist [data-myd-quick-add]');
      if (!btn || btn.disabled) return;
      var handle = btn.getAttribute('data-product-handle');
      var qa = document.querySelector('myd-search-quick-add');
      if (!handle || !qa || typeof qa.open !== 'function') return;
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      try {
        if (typeof window.MYDAccountHideLoader === 'function') window.MYDAccountHideLoader();
      } catch (e) {}
      qa.open(handle, {
        fromCart: false,
        rating: typeof qa.readRatingFromTrigger === 'function' ? qa.readRatingFromTrigger(btn) : null
      });
    },
    true
  );
})();
